import client from "@/client/publicApiClient.ts";
import { useQuery, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { GetForgotPasswordTokenFundamentalsQueryResponse, GetForgotPasswordTokenFundamentalsPathParams, GetForgotPasswordTokenFundamentals404 } from "../types/GetForgotPasswordTokenFundamentals";
import type { QueryObserverOptions, UseQueryResult, QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from "@tanstack/react-query";

 type GetForgotPasswordTokenFundamentalsClient = typeof client<GetForgotPasswordTokenFundamentalsQueryResponse, GetForgotPasswordTokenFundamentals404, never>;
type GetForgotPasswordTokenFundamentals = {
    data: GetForgotPasswordTokenFundamentalsQueryResponse;
    error: GetForgotPasswordTokenFundamentals404;
    request: never;
    pathParams: GetForgotPasswordTokenFundamentalsPathParams;
    queryParams: never;
    headerParams: never;
    response: GetForgotPasswordTokenFundamentalsQueryResponse;
    client: {
        parameters: Partial<Parameters<GetForgotPasswordTokenFundamentalsClient>[0]>;
        return: Awaited<ReturnType<GetForgotPasswordTokenFundamentalsClient>>;
    };
};
export const getForgotPasswordTokenFundamentalsQueryKey = (forgotPasswordToken: GetForgotPasswordTokenFundamentalsPathParams["forgotPasswordToken"]) => [{ url: "/api/forgot-password/:forgotPasswordToken", params: { forgotPasswordToken: forgotPasswordToken } }] as const;
export type GetForgotPasswordTokenFundamentalsQueryKey = ReturnType<typeof getForgotPasswordTokenFundamentalsQueryKey>;
export function getForgotPasswordTokenFundamentalsQueryOptions(forgotPasswordToken: GetForgotPasswordTokenFundamentalsPathParams["forgotPasswordToken"], options: GetForgotPasswordTokenFundamentals["client"]["parameters"] = {}) {
    const queryKey = getForgotPasswordTokenFundamentalsQueryKey(forgotPasswordToken);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetForgotPasswordTokenFundamentals["data"], GetForgotPasswordTokenFundamentals["error"]>({
                method: "get",
                url: `/api/forgot-password/${forgotPasswordToken}`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @summary Get
 * @link /api/forgot-password/:forgotPasswordToken
 */
export function useGetForgotPasswordTokenFundamentals<TData = GetForgotPasswordTokenFundamentals["response"], TQueryData = GetForgotPasswordTokenFundamentals["response"], TQueryKey extends QueryKey = GetForgotPasswordTokenFundamentalsQueryKey>(forgotPasswordToken: GetForgotPasswordTokenFundamentalsPathParams["forgotPasswordToken"], options: {
    query?: Partial<QueryObserverOptions<GetForgotPasswordTokenFundamentals["response"], GetForgotPasswordTokenFundamentals["error"], TData, TQueryData, TQueryKey>>;
    client?: GetForgotPasswordTokenFundamentals["client"]["parameters"];
} = {}): UseQueryResult<TData, GetForgotPasswordTokenFundamentals["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getForgotPasswordTokenFundamentalsQueryKey(forgotPasswordToken);
    const query = useQuery({
        ...getForgotPasswordTokenFundamentalsQueryOptions(forgotPasswordToken, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseQueryResult<TData, GetForgotPasswordTokenFundamentals["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}
export const getForgotPasswordTokenFundamentalsSuspenseQueryKey = (forgotPasswordToken: GetForgotPasswordTokenFundamentalsPathParams["forgotPasswordToken"]) => [{ url: "/api/forgot-password/:forgotPasswordToken", params: { forgotPasswordToken: forgotPasswordToken } }] as const;
export type GetForgotPasswordTokenFundamentalsSuspenseQueryKey = ReturnType<typeof getForgotPasswordTokenFundamentalsSuspenseQueryKey>;
export function getForgotPasswordTokenFundamentalsSuspenseQueryOptions(forgotPasswordToken: GetForgotPasswordTokenFundamentalsPathParams["forgotPasswordToken"], options: GetForgotPasswordTokenFundamentals["client"]["parameters"] = {}) {
    const queryKey = getForgotPasswordTokenFundamentalsSuspenseQueryKey(forgotPasswordToken);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetForgotPasswordTokenFundamentals["data"], GetForgotPasswordTokenFundamentals["error"]>({
                method: "get",
                url: `/api/forgot-password/${forgotPasswordToken}`,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @summary Get
 * @link /api/forgot-password/:forgotPasswordToken
 */
export function useGetForgotPasswordTokenFundamentalsSuspense<TData = GetForgotPasswordTokenFundamentals["response"], TQueryKey extends QueryKey = GetForgotPasswordTokenFundamentalsSuspenseQueryKey>(forgotPasswordToken: GetForgotPasswordTokenFundamentalsPathParams["forgotPasswordToken"], options: {
    query?: Partial<UseSuspenseQueryOptions<GetForgotPasswordTokenFundamentals["response"], GetForgotPasswordTokenFundamentals["error"], TData, TQueryKey>>;
    client?: GetForgotPasswordTokenFundamentals["client"]["parameters"];
} = {}): UseSuspenseQueryResult<TData, GetForgotPasswordTokenFundamentals["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getForgotPasswordTokenFundamentalsSuspenseQueryKey(forgotPasswordToken);
    const query = useSuspenseQuery({
        ...getForgotPasswordTokenFundamentalsSuspenseQueryOptions(forgotPasswordToken, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseSuspenseQueryResult<TData, GetForgotPasswordTokenFundamentals["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}